import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface PageSEOData {
  title: Record<string, string>;
  description: Record<string, string>;
  keywords?: Record<string, string>;
  path: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function usePageSEO({
  title,
  description,
  keywords,
  path,
  ogType = 'website',
  jsonLd,
}: PageSEOData) {
  const { lang } = useLanguage();

  useEffect(() => {
    const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://www.juuliang.com';
    const currentTitle = title[lang] ?? title.zh ?? Object.values(title)[0];
    const currentDesc = description[lang] ?? description.zh ?? Object.values(description)[0];
    const canonicalUrl = `${siteUrl}${path}`;

    document.title = currentTitle;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', currentDesc);

    if (keywords) {
      const currentKw = keywords[lang] ?? keywords.zh ?? Object.values(keywords)[0];
      setMeta('keywords', currentKw);
    }

    setMeta('og:title', currentTitle, 'property');
    setMeta('og:description', currentDesc, 'property');
    setMeta('og:type', ogType, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('twitter:card', 'summary_large_image', 'name');

    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    if (jsonLd) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach((s) => s.remove());
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      const ldData = { '@context': 'https://schema.org', ...jsonLd };
      script.textContent = JSON.stringify(ldData, null, 2);
      document.head.appendChild(script);
    }
  }, [lang, title, description, keywords, path, ogType, jsonLd]);
}